import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  Modal,
  Form,
  message,
  Typography,
  Popconfirm,
  Tooltip,
  Badge,
  InputNumber,
  QRCode,
  Row,
  Col,
  Statistic,
  Checkbox,
  Select,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  CopyOutlined,
  DeleteOutlined,
  LinkOutlined,
  EyeOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  GlobalOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { guestLinkService } from "../../../services";
import { companyService } from "../../../services/companyService";
import { authService } from "../../../services/authService";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const GuestLinks = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isQRModalVisible, setIsQRModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [guestLinks, setGuestLinks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [pageCache, setPageCache] = useState({});
  const [stats, setStats] = useState({
    totalLinks: 0,
    activeLinks: 0,
  });
  const [backendStatsData, setBackendStatsData] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [unlimitedSubmissions, setUnlimitedSubmissions] = useState(false);

  // Get current user info
  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = currentUser?.role === "super_admin";

  // Load companies for super_admin
  const loadCompanies = async () => {
    if (!isSuperAdmin) return;

    try {
      setCompaniesLoading(true);
      const response = await companyService.getCompanies(1, 1000);
      if (response.status) {
        setCompanies(response.data.companies || []);
      }
    } catch (error) {
      console.error("Load companies error:", error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // Load guest links with caching
  const loadGuestLinks = async (
    page = 1,
    pageSize = 10,
    forceRefresh = false,
  ) => {
    try {
      setLoading(true);

      // Create a cache key based on page, pageSize, and search
      const cacheKey = `${page}-${pageSize}-${searchText}`;

      // Check if we have cached data for this page and not forcing refresh
      if (!forceRefresh && pageCache[cacheKey]) {
        setGuestLinks(pageCache[cacheKey].data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: pageCache[cacheKey].total,
        });
        setLoading(false);
        return;
      }

      const response = await guestLinkService.getGuestLinks(
        page,
        pageSize,
        searchText,
      );

      if (response.status) {
        // Store in cache
        setPageCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: response.data.guestLinks || [],
            total: response.data.pagination?.totalItems || 0,
          },
        }));

        setGuestLinks(response.data.guestLinks || []);
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: pageSize,
          total: response.data.pagination?.totalItems || 0,
        });
      } else {
        message.error(response.message || "Failed to load guest links");
        setGuestLinks([]);
        setPagination({
          current: 1,
          pageSize: pageSize,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Load guest links error:", error);
      message.error("Failed to load guest links");
      setGuestLinks([]);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await guestLinkService.getGuestLinkStats();
      if (response.status) {
        setStats(response.data);
        setStats({
          activeLinks: response?.data?.activeLinks,
          totalLinks: response?.data?.totalLinks,
        });
        setBackendStatsData(response.data);
      }
    } catch (error) {
      console.error("Load stats error:", error);
    }
  };

  // Initial load on component mount
  useEffect(() => {
    loadGuestLinks(1, pagination.pageSize);
    loadStats();
    if (isSuperAdmin) {
      loadCompanies();
    }
  }, []);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Clear all cache when search changes
      setPageCache({});
      loadGuestLinks(1, pagination.pageSize);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset company filter when user is not super_admin
  useEffect(() => {
    if (!isSuperAdmin && companyFilter !== "all") {
      setCompanyFilter("all");
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const findCompanyStats = backendStatsData?.companyWiseStats?.filter(
      (c) => c.companyId == companyFilter,
    );

    if (findCompanyStats && findCompanyStats?.length > 0) {
      setStats({
        activeLinks: findCompanyStats[0]?.activeLinks,
        totalLinks: findCompanyStats[0]?.totalLinks,
      });
    } else {
      setStats({
        activeLinks: backendStatsData?.activeLinks,
        totalLinks: backendStatsData?.totalLinks,
      });
    }
  }, [companyFilter]);

  // Format relative time safely
  const formatRelativeTime = (date) => {
    try {
      return dayjs(date).fromNow();
    } catch (error) {
      console.error("Error formatting relative time:", error);
      return "";
    }
  };

  const columns = [
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
      render: (token) => <Text code>{token.substring(0, 8)}...</Text>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <Space>
          <div>
            <div className="font-medium">{title}</div>
            {record.description && (
              <Tooltip title={record.description}>
                <Text type="secondary" style={{ fontSize: "12px" }} ellipsis>
                  {record.description}
                </Text>
              </Tooltip>
            )}
          </div>
        </Space>
      ),
    },

    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (date) => (
        <div>
          <div>{dayjs(date).format("MMM D, YYYY")}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatRelativeTime(date)}
          </Text>
        </div>
      ),
    },
    {
      title: "Max Submissions",
      dataIndex: "maxSubmissions",
      key: "maxSubmissions",
      render: (maxSubmissions, record) => (
        <Space>
          {maxSubmissions === -1 ? (
            <Tag icon={<GlobalOutlined />} color="purple">
              Unlimited
            </Tag>
          ) : (
            <Text>{maxSubmissions}</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Badge
            status={record.isActive ? "success" : "error"}
            text={record.isActive ? "Active" : "Inactive"}
          />
          <Tag color={record.used ? "green" : "blue"}>
            {record.used ? "Used" : "Unused"}
          </Tag>
          <Tag color={record.submissionCount > 0 ? "orange" : "default"}>
            {record.submissionCount} submissions
          </Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Copy Link">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyLink(record.fullUrl)}
            />
          </Tooltip>
          <Tooltip title="Show QR Code">
            <Button
              icon={<QrcodeOutlined />}
              size="small"
              onClick={() => handleShowQRCode(record)}
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Popconfirm
            title={record.isActive ? "Deactivate Link" : "Activate Link"}
            description={`Are you sure you want to ${
              record.isActive ? "deactivate" : "activate"
            } this link?`}
            onConfirm={() => handleToggleActive(record.id, !record.isActive)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title={record.isActive ? "Deactivate" : "Activate"}>
              <Button
                icon={<LinkOutlined />}
                size="small"
                type={record.isActive ? "default" : "primary"}
                danger={record.isActive}
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title="Delete Link"
            description="Are you sure you want to delete this link? This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleGenerateLink = () => {
    setIsModalVisible(true);
    setUnlimitedSubmissions(false);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Prepare data for API
      const submitData = {
        title: values.title,
        description: values.description,
        expiresIn: values.expiresIn,
        maxSubmissions: unlimitedSubmissions ? -1 : values.maxSubmissions,
      };

      // Add companyId for super_admin if selected
      if (isSuperAdmin && values.companyId) {
        submitData.companyId = values.companyId;
      }

      const response = await guestLinkService.createGuestLink(submitData);

      if (response.status) {
        message.success("Guest link created successfully!");
        setIsModalVisible(false);
        form.resetFields();
        setUnlimitedSubmissions(false);
        // Clear cache and reload first page
        setPageCache({});
        loadGuestLinks(1, pagination.pageSize);
        loadStats();
      } else {
        message.error(response.message || "Failed to create guest link");
      }
    } catch (error) {
      console.error("Create guest link error:", error);
      message.error(error.message || "Failed to create guest link");
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setUnlimitedSubmissions(false);
  };

  const handleCopyLink = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        message.success("Link copied!");
      } else {
        // Fallback for HTTP / unsupported browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        message.success("Link copied!");
      }
    } catch (err) {
      console.error("Copy failed", err);
      message.error("Failed to copy link");
    }
  };

  const handleShowQRCode = (link) => {
    setSelectedLink(link);
    setIsQRModalVisible(true);
  };

  const handleViewDetails = (link) => {
    setSelectedLink(link);
    setIsDetailsModalVisible(true);
  };

  const handleToggleActive = async (linkId, isActive) => {
    try {
      const response = await guestLinkService.updateGuestLink(linkId, {
        isActive,
      });
      if (response.status) {
        message.success(
          `Link ${isActive ? "activated" : "deactivated"} successfully!`,
        );
        // Clear cache and reload current page
        setPageCache({});
        loadGuestLinks(pagination.current, pagination.pageSize);
        loadStats();
      } else {
        message.error(
          response.message ||
            `Failed to ${isActive ? "activate" : "deactivate"} link`,
        );
      }
    } catch (error) {
      console.error("Toggle active error:", error);
      message.error(
        error.message ||
          `Failed to ${isActive ? "activate" : "deactivate"} link`,
      );
    }
  };

  const handleDelete = async (linkId) => {
    try {
      const response = await guestLinkService.deleteGuestLink(linkId);
      if (response.status) {
        message.success("Link deleted successfully!");
        // Clear cache and reload
        setPageCache({});
        // If this was the last item on the page, go to previous page
        if (guestLinks.length === 1 && pagination.current > 1) {
          await loadGuestLinks(pagination.current - 1, pagination.pageSize);
        } else {
          await loadGuestLinks(pagination.current, pagination.pageSize);
        }
        loadStats();
      } else {
        message.error(response.message || "Failed to delete link");
      }
    } catch (error) {
      console.error("Delete link error:", error);
      message.error(error.message || "Failed to delete link");
    }
  };

  const handleTableChange = (newPagination) => {
    loadGuestLinks(newPagination.current, newPagination.pageSize);
  };

  const handleRefresh = () => {
    // Clear cache for current page and force refresh
    const currentCacheKey = `${pagination.current}-${pagination.pageSize}-${searchText}`;
    setPageCache((prev) => {
      const newCache = { ...prev };
      delete newCache[currentCacheKey];
      return newCache;
    });
    loadGuestLinks(pagination.current, pagination.pageSize, true);
    loadStats();
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleCompanyFilter = (value) => {
    setCompanyFilter(value);
  };

  const isLinkExpired = (expiresAt) => {
    return dayjs(expiresAt).isBefore(dayjs());
  };

  const getLinkStatus = (link) => {
    if (!link.isActive) return { status: "error", text: "Inactive" };
    if (isLinkExpired(link.expiresAt))
      return { status: "warning", text: "Expired" };
    if (link.used) return { status: "success", text: "Used" };
    return { status: "processing", text: "Active" };
  };

  const handleUnlimitedChange = (e) => {
    setUnlimitedSubmissions(e.target.checked);
    if (e.target.checked) {
      form.setFieldValue("maxSubmissions", undefined);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2}>Guest Links</Title>
          <Text type="secondary">
            Generate and manage guest submission links with QR codes
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="large"
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleGenerateLink}
            size="large"
          >
            Generate Link
          </Button>
        </Space>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={4}>
          <Card className="text-center">
            <Statistic
              title="Total Links"
              value={stats.totalLinks}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="text-center">
            <Statistic
              title="Active Links"
              value={stats.activeLinks}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <div className="flex justify-between mb-4 flex-wrap gap-2">
          <Input
            placeholder="Search by token, title, or description..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 400 }}
            size="large"
            allowClear
          />
          <Space>
            {/* Company Filter - Only show for super_admin */}
            {isSuperAdmin && (
              <Select
                value={companyFilter}
                onChange={handleCompanyFilter}
                style={{ width: 200 }}
                size="large"
                placeholder="Filter by Company"
                loading={companiesLoading}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                <Option value="all">All Companies</Option>
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={guestLinks}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} links`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          rowKey="id"
        />
      </Card>

      {/* Generate Link Modal */}
      <Modal
        title="Generate Guest Link"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Generate Link"
        cancelText="Cancel"
        width={600}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" name="generateLinkForm">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please input a title!" }]}
          >
            <Input
              size="large"
              placeholder="Enter link title (e.g., Customer Testimonials)"
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Describe what this link is for (optional)..."
            />
          </Form.Item>

          {/* Company selection for super_admin */}
          {isSuperAdmin && (
            <Form.Item name="companyId" label="Company">
              <Select
                size="large"
                placeholder="Select company (optional)"
                loading={companiesLoading}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="expiresIn"
            label="Expires In (days)"
            rules={[{ required: true, message: "Please set expiration!" }]}
          >
            <InputNumber
              min={1}
              max={365}
              defaultValue={30}
              style={{ width: "100%" }}
              size="large"
            />
          </Form.Item>

          <Form.Item label="Maximum Submissions">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Checkbox
                checked={unlimitedSubmissions}
                onChange={handleUnlimitedChange}
              >
                <Space>
                  <GlobalOutlined />
                  Unlimited submissions
                </Space>
              </Checkbox>

              {!unlimitedSubmissions && (
                <Form.Item
                  name="maxSubmissions"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message:
                        "Please set maximum submissions or select unlimited!",
                    },
                  ]}
                  initialValue={50}
                >
                  <InputNumber
                    min={1}
                    max={10000}
                    style={{ width: "100%" }}
                    size="large"
                    placeholder="Enter maximum number of submissions"
                  />
                </Form.Item>
              )}
            </Space>
            <Text
              type="secondary"
              style={{ fontSize: "12px", marginTop: "8px", display: "block" }}
            >
              {unlimitedSubmissions
                ? "This link will accept unlimited video submissions until it expires"
                : "Maximum number of video submissions allowed through this link"}
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Guest Link Details"
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedLink && (
          <div className="space-y-4">
            <div>
              <Text strong>Token:</Text>
              <div>
                <Text code>{selectedLink.token}</Text>
              </div>
            </div>

            <div>
              <Text strong>Title:</Text>
              <div>{selectedLink.title}</div>
            </div>

            {selectedLink.description && (
              <div>
                <Text strong>Description:</Text>
                <div>{selectedLink.description}</div>
              </div>
            )}

            {selectedLink.company && (
              <div>
                <Text strong>Company:</Text>
                <div>
                  <Space>
                    <BankOutlined style={{ color: "#1890ff" }} />
                    <div>
                      <div className="font-medium">
                        {selectedLink.company.name}
                      </div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ID: {selectedLink.companyId}
                      </Text>
                    </div>
                  </Space>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text strong>Created:</Text>
                <div>
                  {dayjs(selectedLink.createdAt).format("MMM D, YYYY HH:mm")}
                </div>
              </div>
              <div>
                <Text strong>Expires:</Text>
                <div>
                  {dayjs(selectedLink.expiresAt).format("MMM D, YYYY HH:mm")}
                </div>
                <Text type="secondary">
                  {formatRelativeTime(selectedLink.expiresAt)}
                </Text>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text strong>Max Submissions:</Text>
                <div>
                  {selectedLink.maxSubmissions === -1 ? (
                    <Tag icon={<GlobalOutlined />} color="purple">
                      Unlimited
                    </Tag>
                  ) : (
                    selectedLink.maxSubmissions
                  )}
                </div>
              </div>
              <div>
                <Text strong>Current Submissions:</Text>
                <div>{selectedLink.submissionCount}</div>
              </div>
            </div>

            <div>
              <Text strong>Status:</Text>
              <div>
                <Badge
                  status={getLinkStatus(selectedLink).status}
                  text={getLinkStatus(selectedLink).text}
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <Button
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(selectedLink.fullUrl)}
              >
                Copy Link
              </Button>
              <Button
                icon={<QrcodeOutlined />}
                onClick={() => {
                  setIsDetailsModalVisible(false);
                  handleShowQRCode(selectedLink);
                }}
              >
                Show QR Code
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* QR Code Modal */}
      <Modal
        title="QR Code"
        open={isQRModalVisible}
        onCancel={() => setIsQRModalVisible(false)}
        footer={null}
        width={400}
      >
        {selectedLink && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <QRCode value={selectedLink.fullUrl} size={200} level="H" />
            </div>

            <div>
              <Text strong>Token:</Text>
              <div>
                <Text code>{selectedLink.token.substring(0, 8)}...</Text>
              </div>
            </div>

            <div>
              <Text strong>Title:</Text>
              <div>{selectedLink.title}</div>
            </div>

            <div>
              <Text strong>Max Submissions:</Text>
              <div>
                {selectedLink.maxSubmissions === -1 ? (
                  <Tag
                    icon={<GlobalOutlined />}
                    color="purple"
                    style={{ margin: 0 }}
                  >
                    Unlimited
                  </Tag>
                ) : (
                  selectedLink.maxSubmissions
                )}
              </div>
            </div>

            <div className="flex justify-center space-x-2">
              <Button
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(selectedLink.fullUrl)}
              >
                Copy Link
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GuestLinks;
